/**
 * The hooks the UI consumes. `useChat` exposes top-level state plus every
 * action (for the conversation list and the nav badge); `useMessages` selects
 * one peer's thread.
 *
 * Reactive state comes from the runtime's store, which is per-provider — so
 * unlike a module singleton, these only work under `<ChatProvider>`.
 */
import { useContext } from "react"
import { useStore } from "zustand"
import { ChatRuntimeContext } from "../ChatProvider"
import type { ChatRuntime } from "../runtime"
import { hasUnread, threadFor, typingIn } from "../runtime/selectors"
import type { ChatState } from "../runtime/store"
import type { Message } from "../types"
import type { Jid } from "../xmpp/jid"

export function useChatRuntime(): ChatRuntime {
  const runtime = useContext(ChatRuntimeContext)
  if (!runtime) {
    throw new Error("chat hooks must be used inside <ChatProvider>")
  }
  return runtime
}

/** Subscribe to a slice of chat state. */
export function useChatState<T>(selector: (state: ChatState) => T): T {
  return useStore(useChatRuntime().store, selector)
}

export function useChat() {
  const { actions } = useChatRuntime()

  return {
    connState: useChatState((state) => state.connState),
    conversations: useChatState((state) => state.conversations),
    hasUnread: useChatState(hasUnread),
    refusal: useChatState((state) => state.refusal),
    retractionWindowHours: useChatState((state) => state.retractionWindowHours),
    ...actions,
  }
}

/**
 * Whether the session holds a token — the gate for queries that go through the
 * runtime's HTTP actions. Those reject before auth, and the rejection is not
 * retried, so a query that ran early would stay failed under the same key.
 * `selfJid` is set in the same step as the token.
 */
export function useIsChatAuthenticated(): boolean {
  return useChatState((state) => state.selfJid !== undefined)
}

/** One peer's thread, oldest-first. */
export function useMessages(peer?: Jid): Message[] {
  return useChatState((state) => threadFor(state, peer))
}

/** Handles typing in a peer's conversation right now, in the order they started. */
export function useTyping(peer?: Jid): string[] {
  return useChatState((state) => typingIn(state, peer))
}
